import React, { useMemo, useState } from 'react';

import { Box, Button, createStyles, Group, Text, Tooltip } from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconAlertTriangle, IconClipboardText, IconGift } from '@tabler/icons-react';
import KiloButton from 'components/KiloButton';
import Menu, { MenuItemProps } from 'components/Menu';
import ModalLateCancelNoShow from 'components/ModalLateCancelNoShow';
import ReservationAlertBadges from 'components/ReservationAlertBadges/ReservationAlertBadges';
import dayjs from 'dayjs';
import useUpdateAthleteClass from 'hooks/athleteClass/useUpdateAthleteClass';
import useAthletePackageAllLimitedCreditsUsed from 'hooks/athletePackage/useAthletePackageAllLimitedCreditsUsed';
import useLastCreditAlertBuilder from 'hooks/athletePackage/useLastCreditAlertBuilder';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import ReservationStatusDrawer from 'pages/private/ownerMobile/Schedule/components/ReservationStatusDrawer';
import { useNavigate } from 'react-router-dom';
import { AthleteKiloClassWithAthleteAndWaitlistOrder } from 'types';
import { AthleteKiloClassWithAthlete } from 'types/generated/models/AthleteKiloClassWithAthlete';
import { KiloClassRetrieve } from 'types/generated/models/KiloClassRetrieve';
import { LastCreditAlertContent } from 'utils/last-credit-alert';
import notificationHandler from 'utils/notifications';

import ModalAthleteClassCreateBulk from '../../ModalAthleteClassCreateBulk';
import ModalAthleteClassDelete from '../../ModalAthleteClassDelete';

const useStyles = createStyles(theme => ({
  row: {
    display: 'grid',
    alignItems: 'center',
    padding: theme.spacing.xs,
    gridTemplateColumns: 'min-content auto auto',
    gridTemplateRows: 'auto',
    gap: theme.spacing.sm,
    borderTop: `1px solid ${theme.colors.gray[3]}`,
  },
}));

interface IReservedAndCheckedInAthleteProps {
  athleteKiloClass: AthleteKiloClassWithAthleteAndWaitlistOrder;
  index: number;
  kiloClass: KiloClassRetrieve;
  permission?: boolean;
  setLastCreditAlert: React.Dispatch<React.SetStateAction<LastCreditAlertContent | null>>;
}

const ReservedAndCheckedInAthlete = ({
  athleteKiloClass,
  index,
  kiloClass,
  permission = true,
  setLastCreditAlert,
}: IReservedAndCheckedInAthleteProps): JSX.Element => {
  const { classes, theme } = useStyles();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, false);
  const navigate = useNavigate();
  const staffTracking = useStaffTracking();

  const [isLateCancel, setIsLateCancel] = useState<boolean>(true);
  const [openedTooltip, setOpenedTooltip] = useState(false);
  const [openedBirthdayTooltip, setOpenedBirthdayTooltip] = useState(false);
  const [openedNotesTooltip, setOpenedNotesTooltip] = useState(false);

  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [statusModalOpened, { open: openStatusModal, close: closeStatusModal }] = useDisclosure(false);
  const [createBulkModalOpened, { open: openCreateBulkModal, close: closeCreateBulkModal }] =
    useDisclosure(false);
  const [
    lateCancelNoShowModalOpened,
    { open: openLateCancelNoShowModal, close: closeLateCancelNoShowModal },
  ] = useDisclosure(false);

  const updateAthleteKiloClassMutation = useUpdateAthleteClass();
  const getAthletePackageAllLimitedCreditsUsed = useAthletePackageAllLimitedCreditsUsed();
  const buildLastCreditAlert = useLastCreditAlertBuilder('staff');

  const hasPastDueBills = athleteKiloClass.hasPastDueBills;
  const hasUnsignedDocument = athleteKiloClass.hasUnsignedDocuments;
  const isCredited = athleteKiloClass.isCredited;
  const hasNotifcation = hasPastDueBills || hasUnsignedDocument || !isCredited;

  const isBirthday = athleteKiloClass.athlete.dob
    ? dayjs(new Date(athleteKiloClass.athlete.dob)).format('MM-DD') ===
      dayjs(new Date(kiloClass.startDate)).format('MM-DD')
    : false;

  const getNotificationMessage = (): string => {
    let notification = '';
    if (hasPastDueBills) {
      notification += ' UNPAID INVOICE';
    }
    if (hasUnsignedDocument) {
      if (notification.length > 1) {
        notification += ' & ';
      }
      notification += ' UNSIGNED DOCUMENT';
    }
    if (!isCredited) {
      if (notification.length > 1) {
        notification += ' & ';
      }
      notification += 'UNCREDITED RESERVATION';
    }
    return `This profile has one or more issues that require attention: ${notification}.`;
  };

  const handleUpdate = (akc: AthleteKiloClassWithAthlete): void => {
    const { successNotification, errorNotification } = notificationHandler();
    const payload = {
      ...akc,
      isCheckedIn: !akc.isCheckedIn,
      athlete: akc.athlete.athleteId,
      chargeNow: null,
      chargeDate: null,
      chargeFee: null,
      taxRate: null,
      deductCredit: null,
      isKioskSelfService: false,
    };
    updateAthleteKiloClassMutation.mutate(payload, {
      onSuccess: response => {
        successNotification(
          'Athlete reservation update success',
          `${akc.athlete.fullName}'s reservation successfully updated.`,
        );
        if (kiloClass.kiloClassId && payload.isCheckedIn) {
          staffTracking.trackAthleteCheckedIn(
            akc.athlete.athleteId,
            akc.athlete.fullName,
            kiloClass.kiloClassId,
            kiloClass.kiloClassType.name,
            'class',
          );
        }

        if (
          (payload.isCheckedIn || payload.isLateCancel || payload.isNoShow) &&
          response.data?.creditDispensation
        ) {
          getAthletePackageAllLimitedCreditsUsed.mutate(
            { creditDispensation: response.data.creditDispensation },
            {
              onSuccess: res => {
                const alert = buildLastCreditAlert(res.data, akc.athlete.firstName);
                if (alert) {
                  setLastCreditAlert(alert);
                }
              },
              onError: error => {
                errorNotification(
                  'Error to check if all limited credits used',
                  error.response?.data.message,
                );
              },
            },
          );
        }
      },
      onError: error => {
        errorNotification('Athlete reservation update failed', error.response?.data.message);
      },
    });
  };

  const handleNameClick = (): void => {
    navigate(`/users/athletes/${athleteKiloClass.athlete.athleteId}/general-info`);
  };

  const handleLateCancel = (): void => {
    setIsLateCancel(true);
    openLateCancelNoShowModal();
  };

  const handleNoShow = (): void => {
    setIsLateCancel(false);
    openLateCancelNoShowModal();
  };

  const menuItems: MenuItemProps[] = useMemo(
    () => {
      let items: MenuItemProps[] = [
        {
          title: 'Bulk Reservation',
          onClick: () => openCreateBulkModal(),
        },
        {
          title: 'Remove Reservation',
          onClick: () => openDeleteModal(),
        },
      ];
      if (isCredited) {
        items.push(
          ...[
            {
              title: 'Mark Late Cancel',
              onClick: () => handleLateCancel(),
              disabled: athleteKiloClass.isCheckedIn,
            },
            {
              title: 'Mark No Show',
              onClick: () => handleNoShow(),
              disabled: athleteKiloClass.isCheckedIn,
            },
          ],
        );
      }
      return items;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [athleteKiloClass.isCheckedIn, isCredited],
  );

  const athleteAppointmentReservationTitle = useMemo(() => {
    if (athleteKiloClass.isCheckedIn) {
      return 'Checked In';
    }

    if (!athleteKiloClass.isCheckedIn && athleteKiloClass.isReserved) {
      if (athleteKiloClass.isUncreditedReservation) {
        return 'Reserved (Uncredited)';
      }
      return 'Reserved';
    }

    return null;
  }, [athleteKiloClass.isCheckedIn, athleteKiloClass.isReserved, athleteKiloClass.isUncreditedReservation]);

  return (
    <>
      <Box className={classes.row} bg={athleteKiloClass.isCheckedIn ? theme.colors.green[1] : 'default'}>
        <Text c='dimmed' size='sm'>
          {index + 1}
        </Text>
        {/* Wraps rather than noWrap: the drawer is narrow, and with two badges a long name would
            otherwise compress onto two lines. Letting the row wrap keeps the name intact and drops
            the badges to a second line only when there genuinely isn't room. */}
        <Group position='left' spacing='xs' sx={{ rowGap: 4 }}>
          <Text
            weight='bold'
            underline
            sx={{ cursor: 'pointer', width: 'fit-content', whiteSpace: 'nowrap' }}
            onClick={() => handleNameClick()}
          >
            {athleteKiloClass.athlete.fullName}
          </Text>
          {hasNotifcation && isDesktop && (
            <Tooltip label={getNotificationMessage()} multiline>
              <IconAlertTriangle size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.red[6]} />
            </Tooltip>
          )}
          {athleteKiloClass.athlete.notes?.length > 0 && isDesktop && (
            <Tooltip label='This profile has one or more notes to review.'>
              <IconClipboardText size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.blue[6]} />
            </Tooltip>
          )}
          {athleteKiloClass.athlete.notes?.length > 0 && !isDesktop && (
            <Tooltip label='This profile has one or more notes to review.' opened={openedNotesTooltip}>
              <Button onClick={() => setOpenedNotesTooltip(o => !o)} variant='subtle' compact>
                <IconClipboardText size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.blue[6]} />
              </Button>
            </Tooltip>
          )}
          {isBirthday && isDesktop && (
            <Tooltip label='This athlete has a birthday today.'>
              <IconGift size={theme.fontSizes.xl} strokeWidth={2} color='green' />
            </Tooltip>
          )}
          {hasNotifcation && !isDesktop && (
            <Tooltip multiline label={getNotificationMessage()} opened={openedTooltip}>
              <Button onClick={() => setOpenedTooltip(o => !o)} variant='subtle' compact>
                <IconAlertTriangle size={theme.fontSizes.xl} strokeWidth={2} color={theme.colors.red[6]} />
              </Button>
            </Tooltip>
          )}
          {isBirthday && !isDesktop && (
            <Tooltip label='This athlete has a birthday today.' opened={openedBirthdayTooltip}>
              <Button onClick={() => setOpenedBirthdayTooltip(o => !o)} variant='subtle' compact>
                <IconGift size={theme.fontSizes.xl} strokeWidth={2} color='green' />
              </Button>
            </Tooltip>
          )}
          <ReservationAlertBadges
            athleteStatus={athleteKiloClass.athlete.athleteStatus}
            isFirstClassVisit={athleteKiloClass.isFirstClassVisit}
            onBadgeViewed={badge =>
              staffTracking.trackReservationAlertBadgeViewed({
                badge,
                surface: 'scheduling',
                athlete_id: athleteKiloClass.athlete.athleteId,
              })
            }
          />
        </Group>

        <Group style={{ justifyContent: 'end' }} noWrap spacing='xs'>
          <Text c='dimmed' size='sm'>
            {athleteAppointmentReservationTitle}
          </Text>

          <Menu
            items={menuItems}
            disabled={(athleteKiloClass.isCheckedIn && !athleteKiloClass.isReserved) || !permission}
            onOpen={!isDesktop ? openStatusModal : undefined}
          />
          <KiloButton
            variant={athleteKiloClass.isCheckedIn ? 'outline' : 'filled'}
            onClick={() => handleUpdate(athleteKiloClass)}
            h={28}
            px={7}
            loading={updateAthleteKiloClassMutation.isLoading}
          >
            <Text>{athleteKiloClass.isCheckedIn ? 'Remove Check In' : 'Check In'}</Text>
          </KiloButton>
        </Group>
      </Box>
      <ModalAthleteClassDelete
        opened={deleteModalOpened}
        handleClose={closeDeleteModal}
        athleteKiloClassId={athleteKiloClass.athleteKiloClassId}
      />
      <ModalAthleteClassCreateBulk
        opened={createBulkModalOpened}
        handleClose={closeCreateBulkModal}
        athleteKiloClass={athleteKiloClass}
        kiloClass={kiloClass}
      />
      <ModalLateCancelNoShow
        opened={lateCancelNoShowModalOpened}
        handleClose={closeLateCancelNoShowModal}
        isLateCancel={isLateCancel}
        eventType='class'
        athleteEventId={athleteKiloClass.athleteKiloClassId}
        athleteId={athleteKiloClass.athlete.athleteId}
        athleteName={`${athleteKiloClass.athlete.firstName} ${athleteKiloClass.athlete.lastName}`}
        eventId={athleteKiloClass.kiloClass}
        eventName={kiloClass.kiloClassType.name}
        gymId={kiloClass.gym}
        cancellationSettings={kiloClass.reservationAndCancellationSettings}
        triggeredBy='calendar'
        setLastCreditAlert={setLastCreditAlert}
      />
      {!isDesktop && (
        <ReservationStatusDrawer
          opened={statusModalOpened}
          athleteKiloClass={athleteKiloClass}
          handleUpdate={() => {
            handleUpdate(athleteKiloClass);
            closeStatusModal();
          }}
          handleLateCancel={handleLateCancel}
          handleNoShow={handleNoShow}
          closeDrawer={closeStatusModal}
        />
      )}
    </>
  );
};

export default ReservedAndCheckedInAthlete;
